import {
  InfoCircleOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  Button,
  Space,
  Select,
  Divider,
  Modal,
  Form,
  Input,
  Typography,
  Alert,
} from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { AppDispatch } from "../helpers/appDispatch";
import { useDispatch } from "react-redux";
import { appSelector } from "../helpers/appSelector";
import { HallFloor, CreateHallFloorPayload } from "../interfaces";
import { createHallFloor, getHallFloors } from "../store/hallFloors";
import HallFloorsList from "../components/hallsFloors/HallFloorsList";
import CreateHallFloorForm from "../components/hallsFloors/CreateHallFloorForm";
import { getHalls } from "../store/halls";

const HallFloors = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    hall,
    halls,
    loading: loadingHalls,
  } = appSelector((state) => state.hallsStore);
  const { school } = appSelector((state) => state.schoolStore);

  const [form] = Form.useForm();

  const [filteredHallFloors, setFilteredHallFloors] = useState<HallFloor[]>([]);
  const [hallOnChangeTriggered, setHallOnChangeTriggered] =
    useState<boolean>(false);
  const [selectedHallId, setSelectedHallId] = useState<string>("");

  useEffect(() => {
    dispatch(getHalls(school?.id?.toString() || ""));
  }, [school]);

  const { floors, loading, isHallFloorSubmitting, success } = appSelector(
    (state) => state.hallFloorStore
  );

  useEffect(() => {
    setFilteredHallFloors(floors);
  }, [floors]);

  useEffect(() => {
    setFilteredHallFloors([]);
  }, [dispatch, hall?.id]);

  useEffect(() => {
    form.resetFields();
    success && onCloseModal();
  }, [success]);

  const initialValues: CreateHallFloorPayload = {
    name: "",
    description: "",
    hall_id: selectedHallId || "",
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOnFinish = (values: CreateHallFloorPayload) => {
    values.hall_id = selectedHallId || "";
    dispatch(createHallFloor(values));
  };

  const onSearch = (value: string) => {
    let filtered = floors.filter((floor: HallFloor) =>
      floor.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredHallFloors(filtered);
  };

  return (
    <>
      <Helmet>
        <title>Admissions Ghana | Hall Floors Setup | Blocks</title>
      </Helmet>
      <div className="padding-box">
        <Content className="site-layout-background site-content">
          <div className="margin-top">
            <Row>
              <Col span={24} md={24} xs={24} sm={24}>
                <Space wrap>
                  <Select
                    loading={loadingHalls}
                    placeholder={"Please choose a hall"}
                    style={{ width: 200 }}
                    onChange={(value) => {
                      dispatch(getHallFloors(value.toString()));
                      setHallOnChangeTriggered(true);
                      setSelectedHallId(value);
                    }}
                  >
                    {halls?.map((hall) => {
                      const floorsCount = hall.floors?.length || 0;
                      return (
                        <Select.Option key={hall.id}>
                          {hall.name} ({floorsCount}{" "}
                          {floorsCount === 1 ? "floor" : "floors"})
                        </Select.Option>
                      );
                    })}
                  </Select>
                  <Button
                    disabled={!hallOnChangeTriggered}
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Add Floor to Selected Hall
                  </Button>
                </Space>
                <Space wrap style={{ float: "right" }}>
                  <Input.Search
                    placeholder="Search by floor"
                    onKeyUp={(event) =>
                      onSearch((event.target as HTMLInputElement).value)
                    }
                  />
                </Space>
              </Col>
              <Divider />
            </Row>
          </div>
          {!hallOnChangeTriggered && (
            <Alert
              message="Kindly select a hall first"
              icon={<InfoCircleOutlined />}
              closable
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
            />
          )}
          <HallFloorsList
            halls={filteredHallFloors}
            loading={loading}
            selectedHallId={selectedHallId}
          />
        </Content>
      </div>

      <CreateHallFloorForm
        open={isModalOpen}
        onCancel={onCloseModal}
        loading={isHallFloorSubmitting}
        form={form}
        initialValues={initialValues}
        onFinish={handleOnFinish}
      />
    </>
  );
};

export default HallFloors;
